import { collection, addDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useEffect, useState } from "react";

export default function NotesPanel({ brand, model, variant }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    if (!brand || !model) return;

    let colRef;
    if (variant) {
      colRef = collection(db, "EIS", brand, "Models", model, "Variants", variant, "Notes");
    } else {
      colRef = collection(db, "EIS", brand, "Models", model, "Notes");
    }

    const q = query(
      colRef,
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [brand, model, variant]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      let colRef;
      if (variant) {
        colRef = collection(db, "EIS", brand, "Models", model, "Variants", variant, "Notes");
      } else {
        colRef = collection(db, "EIS", brand, "Models", model, "Notes");
      }

      await addDoc(colRef, {
        text: newNote.trim(),
        createdAt: serverTimestamp()
      });
      setNewNote("");
    } catch (err) {
      console.error("Error adding note:", err);
      alert("Failed to save note");
    }
  };

  const handleDeleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      let docRef;
      if (variant) {
        docRef = doc(db, "EIS", brand, "Models", model, "Variants", variant, "Notes", id);
      } else {
        docRef = doc(db, "EIS", brand, "Models", model, "Notes", id);
      }
      await deleteDoc(docRef);
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Add Note Section */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex-shrink-0">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Add Engineering Note</h3>
        <textarea
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
          className="w-full h-24 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all placeholder:text-slate-400 resize-none"
          placeholder="Type your observations here..."
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Post Note
          </button>
        </div>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1">
        {notes.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </div>
            <h3 className="text-sm font-bold text-slate-400">No Notes Yet</h3>
            <p className="text-xs text-slate-300 mt-1">Start adding engineering notes for this model.</p>
          </div>
        ) : (
          notes.map(note => (
            <div key={note.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 group hover:border-indigo-100 transition-all">
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {note.createdAt?.toDate ? note.createdAt.toDate().toLocaleString() : "Just now"}
                </span>
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                  title="Delete Note"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {note.text}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
